import { useSelector } from 'react-redux';
import { Box, Flex, HStack, InputLeftElement, Switch } from '@chakra-ui/react';
import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import { GrPowerReset } from 'react-icons/gr';
import {
    // Button, // "Filtering by" bar
    Input,
    InputGroup,
    InputRightElement,
    Text,
    Tooltip,
} from '@/components/v4';
// import { Chip } from '@/components/v4/ChipInput/Chip'; // "Filtering by" bar
import CollapsibleButtonGroup from 'components/presentational/CollapsibleButtonGroup';
import TableIconButton from 'components/tables/AGGrid/TableIconButton';
import { searchStyling } from 'components/tables/AGGrid/agGridStyles';
// import { getActiveFilterChips } from '../utils/filters'; // "Filtering by" bar

// const OVERSTAMP_SWITCH_ID = 'bulk-overstamp-differs'; // "Overstamp differs" toggle
const INELIGIBLE_SWITCH_ID = 'bulk-show-ineligible';

/** A small labelled switch, sized to sit in the toolbar band. */
const ToolbarSwitch = ({ id, label, tooltip, isChecked, onChange }) => {
    const { colorScheme } = useSelector((state) => state.settings);
    return (
        <Tooltip label={tooltip} hasArrow placement="top">
            <HStack spacing={2} flexShrink={0}>
                <Text
                    as="label"
                    htmlFor={id}
                    fontSize="xs"
                    m={0}
                    cursor="pointer"
                    whiteSpace="nowrap">
                    {label}
                </Text>
                <Switch
                    id={id}
                    size="sm"
                    colorScheme={colorScheme}
                    isChecked={isChecked}
                    onChange={(e) => onChange(e.target.checked)}
                />
            </HStack>
        </Tooltip>
    );
};

/**
 * The All Numbers toolbar, laid out like the Numbers page table toolbar:
 * actions on the left and search on the right. Column values (country,
 * status, trunk…) are filtered from the column headers, and number patterns
 * (contains, starts with, length, regex…) from the Number column's filter.
 *
 * "Show ineligible" brings back the greyed out numbers that can't be ticked,
 * which are hidden by default. `tip` shows under the controls (the Number
 * filter tip).
 *
 * Commented out until they're wanted, each restored by uncommenting every
 * line marked with its name in this file:
 * - "Filtering by" bar: active filter chips with Clear all (the All Numbers
 *   footer's Reset Filters clears everything meanwhile).
 * - "Overstamp differs" toggle: shows numbers whose Overstamp isn't the
 *   number itself. Its filter column stays in the grid, unused.
 */
const FilterToolbar = ({
    filters,
    onChange,
    // onRemoveFilter, // "Filtering by" bar
    // onClearAll, // "Filtering by" bar
    onOpenPasteList,
    onRefresh,
    showIneligible,
    onShowIneligibleChange,
    tip,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    // const chips = getActiveFilterChips(filters); // "Filtering by" bar
    const borderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';

    const set = (changes) => onChange({ ...filters, ...changes });

    const actions = [
        {
            key: 'paste-list',
            label: 'Paste List',
            tooltip: 'Paste numbers to select them',
            onClick: onOpenPasteList,
        },
    ];

    return (
        <Box
            px={4}
            py="15px"
            borderBottom="1px solid"
            borderColor={borderColor}>
            {/* On a narrow list the search wraps onto its own row rather than
                covering the switch */}
            <Flex align="center" columnGap={3} rowGap={2} wrap="wrap">
                <CollapsibleButtonGroup
                    items={actions}
                    menuLabel="Find Numbers"
                    flexShrink={0}
                    // Just the button's width, not the collapsed menu's 160px
                    minWidth={0}
                />
                <ToolbarSwitch
                    id={INELIGIBLE_SWITCH_ID}
                    label="Show ineligible"
                    tooltip="Show the numbers that can't be selected (assigned to a user, processing, or Teams Phone Mobile)"
                    isChecked={showIneligible}
                    onChange={onShowIneligibleChange}
                />
                {/* "Overstamp differs" toggle
                <ToolbarSwitch
                    id={OVERSTAMP_SWITCH_ID}
                    label="Overstamp differs"
                    tooltip="Show numbers whose Overstamp is not the number itself"
                    isChecked={filters.overstampDiffers}
                    onChange={(checked) => set({ overstampDiffers: checked })}
                /> */}
                {/* Wraps once less than 180px is left: the search's 140px
                    minimum plus refresh */}
                <HStack spacing={0} flex="1 0 180px" justify="flex-end">
                    <InputGroup maxW="240px" minW="140px">
                        <InputLeftElement pointerEvents="none" height="30px">
                            <SearchIcon color="gray.300" />
                        </InputLeftElement>
                        <Input
                            aria-label="Search numbers"
                            placeholder="Search numbers..."
                            inputMode="numeric"
                            sx={{ ...searchStyling, width: '100%' }}
                            // InputGroup only pads Chakra's own Input, not the v4 wrapper
                            pl={8}
                            pr={8}
                            bg={darkMode ? 'dark.bgDark' : 'white'}
                            value={filters.search}
                            onChange={(e) => set({ search: e.target.value })}
                        />
                        {filters.search && (
                            <InputRightElement height="30px">
                                <CloseIcon
                                    boxSize="10px"
                                    cursor="pointer"
                                    color="gray.300"
                                    aria-label="Clear search"
                                    onClick={() => set({ search: '' })}
                                />
                            </InputRightElement>
                        )}
                    </InputGroup>
                    <TableIconButton
                        icon={<GrPowerReset />}
                        onClick={onRefresh}
                        tooltip="Refresh Table"
                    />
                </HStack>
            </Flex>
            {tip}

            {/* "Filtering by" bar
            {chips.length > 0 && (
                <Flex
                    align="center"
                    wrap="wrap"
                    gap={1}
                    mt={3}
                    pt={2}
                    borderTop="1px dashed"
                    borderColor={
                        darkMode ? 'dark.tcap-borderGray' : 'gray.300'
                    }>
                    <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="gray.500"
                        mr={1}>
                        Filtering by
                    </Text>
                    {chips.map((chip, index) => (
                        <Chip
                            key={chip.key}
                            input={chip.label}
                            index={index}
                            onCloseClick={() => onRemoveFilter(chip.key)}
                        />
                    ))}
                    <Button
                        variant="link"
                        size="sm"
                        ml="auto"
                        colorScheme="red"
                        onClick={onClearAll}>
                        Clear all
                    </Button>
                </Flex>
            )} */}
        </Box>
    );
};

export default FilterToolbar;
